<template>
  <v-row>
    <v-col cols="12" md="6">
      <b-input-number
        v-model="form.damageCost"
        :error-messages="getErrorMessages(v$.damageCost)"
        hide-details
        label="Сума збитків, грн"
        placeholder="0"
        rank-spacing
        @blur="v$.damageCost.$touch()"></b-input-number>
    </v-col>

    <v-col cols="12" md="6">
      <b-input
        v-model="form.damageCostBased"
        :error-messages="getErrorMessages(v$.damageCostBased)"
        hide-details
        label="Базовий показник"
        placeholder="Сума договору, балансова вартість..."
        @blur="v$.damageCostBased.$touch()"></b-input>
    </v-col>

    <v-col cols="12" md="6">
      <b-input-number
        v-model="form.damageCostPotential"
        :error-messages="getErrorMessages(v$.damageCostPotential)"
        hide-details
        label="Можливі втрати, грн"
        placeholder="0"
        rank-spacing
        @blur="v$.damageCostPotential.$touch()"></b-input-number>
    </v-col>

    <v-col cols="12" md="6">
      <b-select
        v-model="form.probabilityLevelId"
        :error-messages="getErrorMessages(v$.probabilityLevelId)"
        :items="$directory.get('incidentRiskAssessmentProbabilityLevels')"
        :loading="$loading.isLoading('incidentRiskAssessmentProbabilityLevels')"
        hide-details
        label="Ймовірність реалізації"
        placeholder="Оберіть зі списку"
        @blur="v$.probabilityLevelId.$touch()"
        @focus="$directory.fill('incidentRiskAssessmentProbabilityLevels')">
        <template #selection="{ item }">
          <v-chip
            :style="getIncidentRiskProbabilityStyle(item.raw.id)"
            class="font-weight-bold"
            density="compact"
            rounded="lg"
            size="small"
            variant="flat">
            {{ item.raw.name }}
          </v-chip>
        </template>
      </b-select>
    </v-col>

    <v-col cols="12">
      <b-textarea
        v-model="form.description"
        hide-details
        label="Опис оцінки"
        placeholder="Додайте детальний опис оцінки ризику, щоб інші учасники могли краще зрозуміти ситуацію"
        rows="4"></b-textarea>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { PropType, reactive } from 'vue'
import {
  BInput,
  BInputNumber,
  BSelect,
  BTextarea,
} from 'best-modules/components'
import { getErrorMessages } from 'best-modules/utils'
import { helpers, required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import { Dialog } from '@/dialog'
import { getIncidentRiskProbabilityStyle } from '@/pages/operation-risk/incidents/constants'
import type { IncidentRiskAssessmentCredentials } from '@/utils/types/incident'
import { incidentRiskAssessmentCreate } from '@/requests/incident'
import { setSnackbar } from 'best-modules/plugins'

export default {
  name: 'IncidentRiskAssessment',
  components: {
    BInput,
    BInputNumber,
    BSelect,
    BTextarea,
  },
  props: {
    dialog: {
      type: Object as PropType<Dialog>,
      required: true,
    },
  },
  methods: {
    getErrorMessages,
    getIncidentRiskProbabilityStyle,
  },
  setup(props) {
    const incidentId = props.dialog.dialogData?.incidentId as number
    const form = reactive<IncidentRiskAssessmentCredentials>({
      damageCost: null,
      damageCostPotential: null,
      damageCostBased: null,
      description: null,
      probabilityLevelId: null,
    })

    const rules = {
      damageCost: { required },
      damageCostPotential: {
        isGreaterThanDamageCost: helpers.withMessage(
          'Потенційні втрати не можуть бути менші за фактичні',
          () => {
            if (form.damageCost === null || form.damageCostPotential === null) {
              return true
            }

            return Number(form.damageCostPotential) >= Number(form.damageCost)
          }
        ),
      },
      damageCostBased: { required },
      probabilityLevelId: { required },
    }

    const v$ = useVuelidate(rules, form)

    const submit = () => {
      return incidentRiskAssessmentCreate({
        incidentId,
        ...form,
      }).then(riskAssessment => {
        setSnackbar({
          color: 'success',
          text: 'Оцінку ризику збережено',
        })

        return riskAssessment
      })
    }

    return { form, submit, v$, validationScope: 'incidentRiskAssessment' }
  },
}
</script>
